import { useEffect, useState } from "react";
import type { DeviceSession } from "@centro-cirurgico/contracts";

import { renewDeviceSession } from "../utils/api";
import {
  readDeviceSession,
  readInvalidReason,
  readStoredLinkToken,
  subscribeDeviceSession,
  type SessionInvalidReason
} from "../utils/session";

export interface DeviceSessionState {
  session: DeviceSession | null;
  /** Motivo da última invalidação (revogado/expirado) quando não há sessão. */
  reason: SessionInvalidReason | null;
  /** Renovando a sessão pelo link guardado neste aparelho. */
  renewing: boolean;
}

/** Sessão de dispositivo atual, reativa a limpezas feitas pela API (401) e por outras abas. */
export function useDeviceSession(): DeviceSessionState {
  const [state, setState] = useState<Omit<DeviceSessionState, "renewing">>(() => {
    const session = readDeviceSession();
    return { session, reason: session ? null : readInvalidReason() };
  });
  const [renewing, setRenewing] = useState(() => !state.session && Boolean(readStoredLinkToken()));

  useEffect(() => {
    return subscribeDeviceSession((detail) => {
      setState({ session: detail.session, reason: detail.session ? null : (detail.reason ?? readInvalidReason()) });
    });
  }, []);

  // Sem sessão válida, mas com o link guardado: renova sozinho (sessão vencida após horas de uso).
  useEffect(() => {
    if (state.session || !readStoredLinkToken()) {
      setRenewing(false);
      return;
    }
    let active = true;
    setRenewing(true);
    void renewDeviceSession().finally(() => {
      if (active) setRenewing(false);
    });
    return () => {
      active = false;
    };
  }, [state.session]);

  return { ...state, renewing };
}
